import { cva } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const DANGER = 'bg-danger-subtle text-danger-ink'

/**
 * Badge recipe. Badges are labels, not controls, so there is no hover or focus
 * state. `destructive` is an alias of `danger` for the alias release.
 */
export const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-medium',
  {
    variants: {
      variant: {
        default: 'bg-brand-subtle text-brand-ink',
        secondary: 'bg-surface-2 text-ink',
        outline: 'border border-line-2 text-ink',
        success: 'bg-success-subtle text-success-ink',
        warning: 'bg-warning-subtle text-warning-ink',
        danger: DANGER,
        destructive: DANGER,
        info: 'bg-info-subtle text-info-ink',
        /*
         * Single-emphasis tag (design system spec §2/§4.4): "New", pinned,
         * "#1" — one mark per card, never a status family. Background is a
         * tint of the brand-derived accent-bright role, not the mark colour
         * itself, so small badge text keeps full AA (index.css's
         * --color-accent-tint comment).
         */
        accent: 'bg-accent-tint text-accent-tint-ink',
        /* A numeral pill (a tab's count, a list's total) — same shape as
           every other badge, deliberately neutral so it never competes with
           the accent/status families for attention. */
        count: 'bg-surface-2 text-ink-2 tabular-nums',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * @param {Object} props
 * @param {'default'|'secondary'|'outline'|'success'|'warning'|'danger'|'destructive'|'info'|'accent'|'count'} [props.variant='default']
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function Badge({ className, variant, ...props }) {
  return <div data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge }
